<script setup lang="ts">
import { type PropType } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import GaiaText from '../../base/GaiaText.vue';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaStatus,
  gaiaTracking,
} from '../../themes/tokens';

const props = defineProps({
  /** File name, e.g. "MSA-Kova-AG-signed-2026-02-24.pdf". */
  name: {
    type: String,
    required: true,
  },
  /** Current revision — shows the green mini check next to the name. */
  current: {
    type: Boolean,
    default: false,
  },
  /** Superseded-revision badge label, e.g. "v2 · superseded". */
  revLabel: {
    type: String,
    default: undefined,
  },
  /** Right-aligned meta, e.g. the owning contract's name. */
  metaLabel: {
    type: String,
    default: undefined,
  },
  /** Route the filename opens. Set, it renders as a real link (new tab works); unset, it emits `open`. */
  to: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    default: undefined,
  },
});

const emit = defineEmits<{ open: [] }>();

const border = gaiaNeutral.borderCard;
const borderHover = gaiaNeutral.borderHoverSoft;
const surface = gaiaNeutral.surface;
// The filename is a <button> with a :hover, and the revision chip is a fixed
// 19px bordered box — both take their type through v-bind rather than props.
const nameSize = gaiaFontSize.body;
const nameWeight = String(gaiaFontWeight.semibold);
const revTracking = gaiaTracking.labelSoft;
const subtle = gaiaNeutral.textSubtle;
const accent = gaiaAccent.base;
const success = gaiaStatus.success;
</script>

<template>
  <!-- Linked contract-file card from the entity detail Files tab: name +
       current-revision check (or superseded badge), meta label, #trailing
       menu trigger, default slot for the mentions row, #state for the inline
       unlink confirm / error strips. The name's link stretches over the whole
       card, so anywhere on it opens the record (and cmd-click still works). -->
  <n-flex vertical :size="11" class="gaia-file-link-card">
    <n-flex align="center" :size="10" :wrap="false">
      <svg
        width="15"
        height="15"
        viewBox="0 0 16 16"
        fill="none"
        :stroke="subtle"
        stroke-width="1.4"
        stroke-linecap="round"
        stroke-linejoin="round"
        :style="{ flex: 'none' }"
      >
        <path d="M4 2.5h5l3 3v8H4z" />
        <path d="M9 2.5v3h3" />
      </svg>
      <router-link v-if="props.to" :to="props.to" class="name" :title="props.name">
        {{ props.name }}
      </router-link>
      <button v-else type="button" class="name" :title="props.name" @click="emit('open')">
        {{ props.name }}
      </button>
      <n-flex
        v-if="props.current"
        align="center"
        justify="center"
        :size="0"
        :wrap="false"
        class="current"
        title="Current revision"
      >
        <svg
          width="8"
          height="8"
          viewBox="0 0 10 10"
          fill="none"
          :stroke="surface"
          stroke-width="1.8"
          stroke-linecap="round"
        >
          <path d="M2 5.2 4 7.2 8 3" />
        </svg>
      </n-flex>
      <gaia-text v-else-if="props.revLabel" class="rev" variant="label" tone="subtle">
        {{ props.revLabel }}
      </gaia-text>
      <n-flex
        align="center"
        :size="8"
        :wrap="false"
        class="raised"
        :style="{ marginLeft: 'auto', flex: 'none' }"
      >
        <gaia-text
          v-if="props.metaLabel"
          size="meta"
          tone="faint"
          :style="{ fontVariantNumeric: 'tabular-nums' }"
        >
          {{ props.metaLabel }}
        </gaia-text>
        <slot name="trailing" />
      </n-flex>
    </n-flex>
    <n-flex align="center" :size="8" :style="{ paddingLeft: '25px' }"><slot /></n-flex>
    <div v-if="$slots.state" class="state raised"><slot name="state" /></div>
  </n-flex>
</template>

<style scoped>
.gaia-file-link-card {
  position: relative;
  border: 1px solid v-bind(border);
  border-radius: 10px;
  background: v-bind(surface);
  padding: 13px 14px 14px;
}

.name {
  min-width: 0;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: v-bind(nameSize);
  font-weight: v-bind(nameWeight);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  text-align: left;
}

a.name {
  color: inherit;
  text-decoration: none;
}

/* The stretched hit area: the name's link covers the card. */
.name::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
}

/* The menu trigger and the confirm strip sit above it, so they stay their own clicks. */
.raised {
  position: relative;
  z-index: 1;
}

.gaia-file-link-card:hover {
  border-color: v-bind(borderHover);
}

.gaia-file-link-card:hover .name {
  color: v-bind(accent);
}

.name:hover {
  color: v-bind(accent);
}

.current {
  width: 14px;
  height: 14px;
  flex: none;
  border-radius: 50%;
  background: v-bind(success);
}

.rev {
  flex: none;
  display: inline-flex;
  align-items: center;
  height: 19px;
  padding: 0 7px;
  border-radius: 6px;
  border: 1px solid v-bind(border);
  /* 10px is a chip-interior metric for a fixed 19px box, not a type step; the
     casing, weight and color come from GaiaText's label variant. */
  font-size: 10px;
  letter-spacing: v-bind(revTracking);
}
</style>
